import * as React from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import apiClient, { getErrorMessage } from '../services/api-client';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import AppConfig from '../services/app-config';
import eventReportService from '../services/event-report';
import { Navigate, useLocation } from 'react-router-dom';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { alpha } from '@mui/material/styles';

function Copyright() {
  return (
    <Typography variant="body2" color="text.secondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://migarita.online">
        MiGarita.Online
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function App({setNavbarState, navbarState}) {
  const [email, setEmail] = React.useState("");
  const [org, setOrg] = React.useState("");
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [password, setPassword] = React.useState("");
  const [severity, setSeverity] = React.useState("warning");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [showBackdrop, setShowBackdrop] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const apiUrl = process.env.REACT_APP_MGO_API_URL;
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  
  function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  
    return JSON.parse(jsonPayload);
  }

  React.useEffect(() => {
    setNavbarState({...navbarState, showBack: false, showOptions: false, showSearch: false})
    try {
      const data = parseJwt(token);
      setEmail(data.email);
      setOrg(data.org);
    } catch (err) {
      setRedirect({ trigger: true, route: '/login' });
    }
  }, [token]);

  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const reset = async () => {
    if (password === "" || confirmPassword === "") {
      setSnackbarOpen(true);
      setSeverity("warning")
      setSnackbarMessage("Porfavor establece una contraseña.");
      return;
    }

    if (password.length < 8) {
      setSnackbarOpen(true);
      setSeverity("warning")
      setSnackbarMessage("Porfavor usa al menos 8 caracteres para tu contraseña.");
      return;
    }

    if (password !== confirmPassword) {
      setSnackbarOpen(true);
      setSeverity("warning")
      setSnackbarMessage("Las contraseñas no son iguales.");
      return;
    }
    setShowBackdrop(true);

    try {
      const res = await apiClient.post('reset/login', { password: password, token: token}, {}, { action: 'reset_password' });
      AppConfig.parseResponse(res);
      if (res.status === 200) {
        setSnackbarOpen(true);
        setSeverity("success");
        setSnackbarMessage("Contraseña restablecida");
        AppConfig.parseResponse(res);
        setShowBackdrop(false);
        eventReportService('password-reset', { email: email, org: org });
        setTimeout(function(){ window.location.href= '/';}, 2000);
      }
    } catch (err) {
      if (err.statusCode === 401 || (err.response && err.response.data && err.response.data.code === 401)) {
        setSnackbarOpen(true);
        setSnackbarMessage('El enlace que has utilizado es incorrecto o ya ha vencido.');
      } else {
        setSnackbarOpen(true);
        setSnackbarMessage('Error al restablecer la contraseña, intenta de nuevo en otro momento.');
      }
      setShowBackdrop(false);
      setTimeout(function(){ window.location.href= '/login';}, 3000);
    }
  }

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const RequirementItem = ({ met, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {met ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      )}
      <Typography variant="caption" sx={{ color: met ? '#2e7d32' : 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
      {redirect.trigger && (
        <Navigate to={redirect.route} replace={true} />
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{mt: "3.2rem"}}>
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Backdrop open={showBackdrop} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: 3,
          backgroundColor: '#e8f5e9', display: 'flex',
          alignItems: 'center', justifyContent: 'center', mb: 2
        }}>
          <ShieldOutlinedIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
        </Box>
        <Typography variant="h5" component="h1" align='center' sx={{ fontWeight: 700, color: '#1a1a1a', mb: 1 }}>
          Restablecer Contraseña
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 320 }}>
          Por favor ingresa tu nueva contraseña para asegurar tu cuenta de residente.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
          Nueva Contraseña
        </Typography>
        <TextField
          size="medium" fullWidth
          type={showPassword ? 'text' : 'password'} id='password'
          placeholder="••••••••"
          variant="outlined" value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{
            mb: 0.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mb: 2.5 }}>
          <RequirementItem met={hasMinLength} label="Mínimo 8 caracteres" />
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
          Confirmar Contraseña
        </Typography>
        <TextField
          size="medium" fullWidth
          type={showConfirmPassword ? 'text' : 'password'} id='confirmPassword'
          placeholder="••••••••"
          variant="outlined" value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                  size="small"
                >
                  {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.03),
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
            Requisitos de Seguridad
          </Typography>
          <RequirementItem met={hasUpperCase} label="Una letra mayúscula" />
          <RequirementItem met={hasNumber} label="Un número" />
          <RequirementItem met={hasSpecialChar} label="Un carácter especial (!@$%&*?&)" />
        </Box>

        <Button
          fullWidth size="large" variant="contained"
          onClick={reset}
          endIcon={<ArrowForwardIcon />}
          sx={{
            py: 1.5, borderRadius: 2, textTransform: 'none',
            fontSize: '1rem', fontWeight: 600,
            backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' }
          }}
        >
          Restablecer Contraseña
        </Button>
      </Box>

      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Copyright />
      </Box>
    </Container>
  );
}